import { softphone, type SoftphoneDiagnose } from "../../services/linkusSdk";
import type { TicketTelefonie } from "./ticketTypen";
import { telefonieSpurenSchnappschuss } from "./telefonieSpuren";

/**
 * Telefonie-Stand fürs Ticket (T-28): SDK-Stand, Anrufe aus Sicht des
 * Softphone-Wrappers, Mikrofon-Freigabe, Geräte-ANZAHL (keine Namen/Labels) und
 * die [telephony]-Konsolenspuren (telefonieSpuren.ts) — asynchron, mit Timeout,
 * wirft nie. Die Anrufe aus der Anrufliste (CallLog) und die Nebenstelle
 * ergänzt der Server beim Anlegen.
 */
// ── Telefonie-Stand beim Melden ────────────────────────────────────────────

type Mikrofon = NonNullable<TicketTelefonie["mikrofon"]>;
type Geraete = NonNullable<TicketTelefonie["geraete"]>;

export type TelefonieQuellen = {
  softphone: () => SoftphoneDiagnose | null;
  mikrofon: () => Promise<Mikrofon>;
  geraete: () => Promise<Geraete | null>;
};

async function mikrofonFreigabe(): Promise<Mikrofon> {
  const perms = typeof navigator !== "undefined" ? navigator.permissions : undefined;
  if (!perms?.query) return "unbekannt";
  const r = await perms.query({ name: "microphone" as PermissionName });
  return r.state === "granted" || r.state === "denied" || r.state === "prompt" ? r.state : "unbekannt";
}

/** Nur die ANZAHL der Ein-/Ausgabegeräte — bewusst keine Namen oder IDs. */
async function geraeteAnzahl(): Promise<Geraete | null> {
  const md = typeof navigator !== "undefined" ? navigator.mediaDevices : undefined;
  if (!md?.enumerateDevices) return null;
  const liste = await md.enumerateDevices();
  return {
    eingaenge: liste.filter((d) => d.kind === "audioinput").length,
    ausgaenge: liste.filter((d) => d.kind === "audiooutput").length,
  };
}

const STANDARD_QUELLEN: TelefonieQuellen = {
  softphone: () => softphone.diagnose(),
  mikrofon: mikrofonFreigabe,
  geraete: geraeteAnzahl,
};

export const TELEFONIE_TIMEOUT_MS = 1500;

/**
 * Sammelt den Telefonie-Stand für das Ticket. Wirft nie und braucht höchstens
 * `timeoutMs` — was bis dahin nicht da ist, fehlt (`unvollstaendig: true`).
 * Liefert null, wenn es nichts Telefonie-Spezifisches zu berichten gibt (SDK
 * nie gestartet, keine Anrufe, keine Spuren) — dann bleibt das Ticket schlank.
 */
export async function sammleTelefonieStand(
  opts: { timeoutMs?: number; quellen?: Partial<TelefonieQuellen> } = {},
): Promise<TicketTelefonie | null> {
  try {
    const q: TelefonieQuellen = { ...STANDARD_QUELLEN, ...opts.quellen };
    const stand: TicketTelefonie = { erfasstUm: new Date().toISOString(), spuren: telefonieSpurenSchnappschuss() };
    try {
      const d = q.softphone();
      if (d) {
        stand.sdk = { status: d.status, fehler: d.fehler, registriert: d.registriert, aktiverAnruf: d.aktiverAnruf };
        stand.softphoneAnrufe = d.anrufe;
        stand.audio = d.audio;
      }
    } catch {
      /* SDK-Diagnose fehlt dann eben */
    }

    const lohntSich =
      (!!stand.sdk && stand.sdk.status !== "idle") || !!stand.softphoneAnrufe?.length || !!stand.spuren?.length;
    if (!lohntSich) return null;

    const asynchron = Promise.allSettled([
      Promise.resolve().then(q.mikrofon).then((m) => { stand.mikrofon = m; }),
      Promise.resolve().then(q.geraete).then((g) => { stand.geraete = g; }),
    ]);
    let timer: ReturnType<typeof setTimeout> | undefined;
    const zeit = new Promise<"timeout">((r) => { timer = setTimeout(() => r("timeout"), opts.timeoutMs ?? TELEFONIE_TIMEOUT_MS); });
    const ergebnis = await Promise.race([asynchron.then(() => "fertig" as const), zeit]);
    if (timer !== undefined) clearTimeout(timer);
    if (ergebnis === "timeout") stand.unvollstaendig = true;
    // Kopie: Späte Antworten nach dem Timeout ändern das Ergebnis nicht mehr.
    return { ...stand };
  } catch {
    return null;
  }
}
